<script setup lang="ts">
import { computed } from 'vue'

import type { AtcDraft, AtcDraftErrors, AtcField } from '../../models/watchlist.types'
import { summarizeConfig } from '../../service/atcConfig'

import { useI18n } from '@/shared/i18n'
import { COUNTRY_CODES } from '@/shared/lib/countries'
import { Badge, Button, Icon, Input, MultiSelect, Switch, type SelectOption } from '@/shared/ui'

const props = defineProps<{
  draft: AtcDraft
  index: number
  expanded: boolean
  errors?: AtcDraftErrors
  submitted: boolean
}>()

const emit = defineEmits<{
  patch: [patch: Partial<AtcDraft>]
  touch: [field: AtcField]
  toggle: []
  remove: []
}>()

const { t } = useI18n()
const regionOptions: SelectOption<string>[] = COUNTRY_CODES.map((code) => ({
  value: code,
  label: code,
}))
const summary = computed(() => summarizeConfig(props.draft))
const title = computed(
  () =>
    props.draft.config_name ||
    t('workspaceDefinitions.watchlist.atc.configurationFallback', { index: props.index + 1 }),
)

function errorFor(field: AtcField): string | undefined {
  const code = props.errors?.[field]
  if (!code || (!props.submitted && !props.draft.touched[field])) return undefined
  return t(`workspaceDefinitions.watchlist.atc.error.${code}`, { min: 1, max: 3000 })
}

function countLabel(count: number): string {
  return count === 1
    ? t('workspaceDefinitions.watchlist.atc.countOne')
    : t('workspaceDefinitions.watchlist.atc.count', { count })
}
</script>

<template>
  <article
    class="atc-card border border-border rounded-md bg-surface"
    :data-testid="`atc-config-${index}`"
  >
    <header class="atc-card__header grid grid-cols-[minmax(0,_1fr)_auto] items-center p-xs">
      <button
        class="atc-card__toggle flex min-w-0 items-center gap-sm text-fg text-start"
        type="button"
        @click="emit('toggle')"
      >
        <span
          class="atc-card__initial inline-grid w-8 h-8 place-items-center rounded-full bg-primary-subtle text-primary font-bold"
          >{{ title.slice(0, 1).toUpperCase() }}</span
        >
        <span class="atc-card__heading grid flex-1 min-w-0">
          <strong>{{ title }}</strong>
          <small>{{ countLabel(summary.advertiserCount) }}</small>
        </span>
        <Badge v-if="errors && Object.keys(errors).length" tone="warning" size="sm">
          {{ t('workspaceDefinitions.watchlist.atc.needsAttention') }}
        </Badge>
        <Icon :name="expanded ? 'chevron-up' : 'chevron-down'" :size="16" />
      </button>
      <Button
        variant="ghost"
        size="sm"
        square
        :aria-label="t('workspaceDefinitions.watchlist.atc.action.remove')"
        data-testid="atc-remove-config"
        @click="emit('remove')"
      >
        <Icon name="x" :size="16" />
      </Button>
    </header>

    <div v-if="expanded" class="atc-card__body grid gap-md p-md border-t border-t-border">
      <div class="atc-card__grid grid grid-cols-2 gap-md max-sm:grid-cols-[minmax(0,_1fr)]">
        <Input
          :model-value="draft.config_name"
          :label="t('workspaceDefinitions.watchlist.atc.fields.configurationName')"
          :placeholder="t('workspaceDefinitions.watchlist.atc.fields.configurationNamePlaceholder')"
          :error="Boolean(errorFor('config_name'))"
          :error-message="errorFor('config_name') ?? ''"
          @update:model-value="emit('patch', { config_name: String($event ?? '') })"
          @blur="emit('touch', 'config_name')"
        />
        <Input
          :model-value="draft.advertiserIdInput"
          :label="t('workspaceDefinitions.watchlist.atc.fields.advertiserId')"
          :placeholder="t('workspaceDefinitions.watchlist.atc.fields.advertiserIdPlaceholder')"
          :error="Boolean(errorFor('advertiser_id'))"
          :error-message="errorFor('advertiser_id') ?? ''"
          @update:model-value="emit('patch', { advertiserIdInput: String($event ?? '') })"
          @blur="emit('touch', 'advertiser_id')"
        />
      </div>

      <button
        type="button"
        class="atc-card__filters-toggle inline-flex items-center justify-self-start gap-2xs text-primary text-[0.75rem] font-semibold"
        @click="emit('patch', { showFilters: !draft.showFilters })"
      >
        <Icon name="filter" :size="14" />
        {{ t('workspaceDefinitions.watchlist.atc.editFilters') }}
        <Icon :name="draft.showFilters ? 'chevron-up' : 'chevron-down'" :size="14" />
      </button>

      <div
        v-if="draft.showFilters"
        class="atc-card__filters grid grid-cols-2 gap-md max-sm:grid-cols-[minmax(0,_1fr)]"
      >
        <Input
          :model-value="draft.advertiser_name ?? ''"
          :label="t('workspaceDefinitions.watchlist.atc.fields.advertiserName')"
          :placeholder="t('workspaceDefinitions.watchlist.atc.fields.advertiserNamePlaceholder')"
          @update:model-value="emit('patch', { advertiser_name: String($event ?? '') })"
        />
        <MultiSelect
          :model-value="draft.regions"
          :options="regionOptions"
          searchable
          :label="t('workspaceDefinitions.watchlist.atc.fields.regions')"
          :placeholder="t('workspaceDefinitions.watchlist.atc.fields.regionsPlaceholder')"
          @update:model-value="emit('patch', { regions: $event.map(String) })"
        />
        <Input
          :model-value="draft.limit"
          type="number"
          :min="1"
          :max="3000"
          :label="t('workspaceDefinitions.watchlist.atc.fields.limit')"
          :error="Boolean(errorFor('limit'))"
          :error-message="errorFor('limit') ?? ''"
          @update:model-value="emit('patch', { limit: Number($event) })"
          @blur="emit('touch', 'limit')"
        />
        <Switch
          :model-value="draft.include_removed"
          :label="t('workspaceDefinitions.watchlist.atc.fields.includeRemoved')"
          @update:model-value="emit('patch', { include_removed: $event })"
        />
      </div>
    </div>
  </article>
</template>

<style scoped>
.atc-card .atc-card__heading strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.atc-card .atc-card__heading small {
  color: var(--ui-color-muted);
}
</style>
